"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";

export type PitchGalleryProps = {
  images: string[];
};

export default function PitchGallery({ images }: PitchGalleryProps) {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const prevImage = () =>
    setCurrentImageIndex((p) => (p - 1 + images.length) % images.length);
  const nextImage = () => setCurrentImageIndex((p) => (p + 1) % images.length);

  return (
    <div className="relative -mt-2 mb-10 sm:-mt-1">
      <div className="group relative aspect-[16/9] w-full overflow-hidden rounded-2xl bg-slate-100 shadow-[0_24px_60px_-28px_rgba(15,23,42,0.35)] ring-1 ring-slate-900/10 sm:aspect-[21/9] lg:aspect-[16/9]">
        <Image
          src={images[currentImageIndex]}
          alt={t("canchaPitchPage.imageAltSlider")}
          fill
          priority
          sizes="(max-width: 1024px) 100vw, 1152px"
          className="object-cover transition-opacity duration-300"
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-slate-950/50 to-transparent"
          aria-hidden
        />
        <button
          type="button"
          onClick={prevImage}
          className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-slate-950/35 text-white shadow-lg backdrop-blur-md transition hover:bg-slate-950/55 sm:left-4 md:opacity-0 md:group-hover:opacity-100"
          aria-label={t("gallery.previousImage")}
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          type="button"
          onClick={nextImage}
          className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-slate-950/35 text-white shadow-lg backdrop-blur-md transition hover:bg-slate-950/55 sm:right-4 md:opacity-0 md:group-hover:opacity-100"
          aria-label={t("gallery.nextImage")}
        >
          <ChevronRight className="h-6 w-6" />
        </button>
        <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setCurrentImageIndex(i)}
              className={`h-2 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${i === currentImageIndex ? "w-8 bg-white shadow" : "w-2 bg-white/55 hover:bg-white/90"}`}
              aria-label={`${t("gallery.imageGallery")} ${i + 1}`}
              aria-pressed={i === currentImageIndex}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
